import { FIRST_INSTANCE, instanceKey, instancePath } from "../funnel/order";
import {
  REPEATABLE_PAGES,
  pageOf,
  pagePath,
  type SitePage,
} from "./registry";

/**
 * ═══ LES PAGES QUE L'ORGANISATEUR PEUT MULTIPLIER ═══
 *
 * Deux familles à ce jour, et la même grammaire pour les deux :
 *
 *   — la SECTION (`/infos`) : « juste une page où l'on met du HTML, et on peut en
 *     ajouter autant que l'on veut sur l'accueil » ;
 *   — la page FORMULAIRE (`/form`) : « comme les sections, on peut ajouter des pages
 *     qui comportent un formulaire que l'on configure ».
 *
 * Chaque exemplaire est une vraie page du site : sa ligne dans le gestionnaire, son
 * nom, son sous-titre, son contenu, sa tuile. Et c'est le MÊME écran qui sert tous
 * les exemplaires d'une famille.
 *
 * ⚠️ CE FICHIER ÉTAIT ÉCRIT POUR UNE SEULE FAMILLE (`infosPages.ts`, la section) et
 * la nommait en dur. La page formulaire est la seconde : tout y est passé au
 * paramètre. Ce qui reste par famille, c'est ce que la famille est seule à porter —
 * le HTML d'une section, le formulaire servi par une page formulaire.
 *
 * ⚠️ LA LISTE DES EXEMPLAIRES A SA PROPRE CLÉ, UNE PAR FAMILLE, PAS UNE PLACE DANS
 * L'ORDRE DES PAGES. `FO_MEETING_V2_PAGES_ORDER` se remet à zéro d'un bouton
 * (« Remettre l'ordre d'origine ») : des exemplaires rangés dedans auraient DISPARU
 * avec l'ordre. Ranger et exister sont deux questions ; deux clés. Et une clé par
 * famille, sinon « 2 » ne dirait pas de quelle page il parle.
 *
 * ⚠️ ELLES NE SE CASCADENT PAS, `COMMON_KEYS` les route au niveau commun : la
 * structure du site est celle de l'événement, une page ne peut pas exister pour les
 * exposants et pas pour les visiteurs.
 */

const upper = (key: string) => key.toUpperCase().replace(/[^A-Z0-9]+/g, "_");

/** La liste des exemplaires ajoutés de cette famille : `"2,3"`. */
export const instancesKey = (page: SitePage) =>
  `FO_MEETING_V2_${upper(page.key)}_PAGES`;

/**
 * Les clés de contenu d'un exemplaire, suffixées par `instanceKey` comme celles de
 * l'étape répétable : la PREMIÈRE page garde les clés nues, ce qui est déjà écrit
 * reste lu.
 */
export const subtitleKey = (page: SitePage, instance?: string) =>
  instanceKey(`FO_MEETING_V2_${upper(page.key)}_SUBTITLE`, instance);
/** Le HTML libre d'une section, l'introduction d'une page formulaire. */
export const htmlKey = (page: SitePage, instance?: string) =>
  instanceKey(`FO_MEETING_V2_${upper(page.key)}_HTML`, instance);
/** Le formulaire du back office que sert une page formulaire. */
export const formIdKey = (page: SitePage, instance?: string) =>
  instanceKey(`FO_MEETING_V2_${upper(page.key)}_ID`, instance);

/** La page multipliable de cette clé, ou rien — pour les deux ancres ci-dessous. */
const repeatable = (key: string): SitePage => {
  const page = REPEATABLE_PAGES.find((p) => p.key === key);
  if (!page) throw new Error(`Aucune page multipliable « ${key} » au registre`);
  return page;
};

/** La section : du HTML libre, habillé par le produit. */
export const INFOS_PAGE = repeatable("infos");
/** La page formulaire : un formulaire du back office, servi hors du tunnel. */
export const FORM_PAGE = repeatable("form");

/**
 * `"2,3"` → les exemplaires AJOUTÉS. La première page n'y figure pas : elle est
 * déclarée au registre, elle existe toujours. Un « 1 » qui s'y serait glissé est
 * jeté : il désignerait la page du registre, et la ferait apparaître deux fois.
 */
export function readInstances(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  const seen = new Set<string>();
  return raw
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .filter((i) => i !== FIRST_INSTANCE)
    .filter((i) => !seen.has(i) && (seen.add(i), true));
}

export function writeInstances(instances: ReadonlyArray<string>): string {
  return instances.join(",");
}

/**
 * ⚠️ UN IDENTIFIANT QUI NE PEUT PAS COLLISIONNER, et qui ne dépend ni de l'heure ni
 * du hasard, les deux sont interdits sur ce dépôt. Le plus petit rang libre, comme
 * pour l'étape répétable ; retirer la page 2 puis en ajouter une REND « 2 », et avec
 * lui tout le contenu que cette page portait. C'est pourquoi le gestionnaire peut
 * promettre que retirer n'efface rien.
 */
export function nextInstance(instances: ReadonlyArray<string>): string {
  const taken = new Set(instances);
  let n = 2;
  while (taken.has(String(n))) n += 1;
  return String(n);
}

/** Un exemplaire ajouté, dans la forme que lisent le menu, les tuiles, le gestionnaire. */
export type AddedPage = SitePage & {
  /** Son rang, celui qui suffixe ses clés. */
  addedInstance: string;
  /** La page du registre qu'il multiplie — le gestionnaire en a besoin pour retirer. */
  addedTo: string;
};

/** Cette ligne du gestionnaire est-elle un exemplaire ajouté, donc retirable ? */
export function isAdded(page: SitePage): page is AddedPage {
  return "addedInstance" in page;
}

/**
 * La page d'un exemplaire. Sans instance (ou la première) : la page du REGISTRE,
 * enrichie de son réglage de sous-titre ; le registre reste son repli.
 *
 * ⚠️ LE NOM PAR DÉFAUT EST NUMÉROTÉ (« Section 2 », « Formulaire 2 ») : deux entrées
 * du même nom dans le sélecteur d'écran sont impossibles à viser (la leçon des étapes
 * répétées, mot pour mot). Il tient jusqu'à ce que l'organisateur écrive le sien,
 * dans le gestionnaire.
 */
export function pageOfInstance(base: SitePage, instance?: string): SitePage {
  if (!instance || instance === FIRST_INSTANCE) {
    return { ...base, subtitleKey: subtitleKey(base) };
  }
  const added: AddedPage = {
    key: instanceKey(base.key, instance),
    name: `${base.name} ${instance}`,
    icon: base.icon,
    /* ⚠️ LES CAPACITÉS DE LA PAGE SUIVENT SES EXEMPLAIRES. Oubliée, la section 2
       n'avait pas d'interrupteur « Public » dans le gestionnaire alors que la 1 en
       avait un : la même page, deux droits, sans que rien ne le dise. Vu à l'écran,
       aucun test ne le voyait. */
    ...(base.publishable ? { publishable: base.publishable } : {}),
    /* Vide, comme celui de la première : une description écrite par le produit sous
       un contenu écrit par l'organisateur ne peut que finir par mentir. */
    subtitle: "",
    subtitleKey: subtitleKey(base, instance),
    path: instancePath(pagePath(base), instance),
    addedInstance: instance,
    addedTo: base.key,
  };
  return added;
}

/**
 * La page multipliable dont cette page-là est un exemplaire — ou elle-même si c'en
 * est une première. Ce que lisent l'écran et le catalogue pour retrouver leur famille.
 */
export function baseOf(page: SitePage): SitePage | undefined {
  return isAdded(page) ? pageOf(page.addedTo) : page;
}

/**
 * La liste déclarée des pages, exemplaires COMPRIS, chacun juste après la page du
 * registre qu'il multiplie : c'est là que l'organisateur l'attend, et c'est la place
 * que `orderedPages` lui rend s'il manque à un ordre enregistré.
 *
 * `read` est la lecture d'un réglage : chaque famille y va chercher SA liste.
 */
export function withAdded(
  pages: ReadonlyArray<SitePage>,
  read: (key: string) => unknown,
): ReadonlyArray<SitePage> {
  return pages.flatMap((page) =>
    page.repeatable
      ? [
          pageOfInstance(page),
          ...readInstances(read(instancesKey(page))).map((i) =>
            pageOfInstance(page, i),
          ),
        ]
      : [page],
  );
}

/**
 * TOUS LES RANGS AJOUTÉS, FAMILLES CONFONDUES — ce que réclament `everySetting`,
 * `translatableSettings` et le récapitulatif.
 *
 * ⚠️ CONFONDRE LES FAMILLES EST ASSUMÉ ICI, et c'est la règle que ces trois-là
 * suivent déjà pour les étapes : une passe d'instance « 2 » engendre les clés
 * suffixées de TOUTES les familles répétables, même quand une seule a cet exemplaire.
 * La clé fantôme est inerte (sans valeur, « Tout traduire » la saute) et le tri fin
 * coûterait à chaque lecteur la connaissance de qui possède quel rang.
 */
export function addedInstances(read: (key: string) => unknown): string[] {
  return [
    ...new Set(
      REPEATABLE_PAGES.flatMap((page) =>
        readInstances(read(instancesKey(page))),
      ),
    ),
  ];
}
